import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../useLanguage';
import logo from '../resources/images/Flex_CTO_cropped.png';

interface HeaderProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const Header = ({ activeSection, onSectionChange }: HeaderProps) => {
  const { isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { id: 'home', label: t.header.home },
    { id: 'about', label: t.header.about },
    { id: 'services', label: t.header.services },
    { id: 'workshops', label: t.header.workshops },
    { id: 'contact', label: t.header.contact },
  ];

  const handleNavClick = (itemId: string) => {
    if (itemId === 'workshops') {
      navigate('/workshops');
    } else {
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => onSectionChange(itemId), 100);
      } else {
        onSectionChange(itemId);
      }
    }
  };

  return (
    <header className="fixed top-0 w-full bg-white/95 dark:bg-dark-900/95 backdrop-blur-lg z-50 border-b border-gray-200/50 dark:border-dark-700/50 shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-3">
            <img src={logo} alt="Flex CTO" className="h-10 w-auto transition-transform hover:scale-105" />
            <div className="hidden sm:block">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">Flex CTO</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Technical Leadership</div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeSection === item.id
                    ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20 shadow-sm'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-dark-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-all duration-200 hover:scale-110"
              title={isDark ? t.header.lightMode : t.header.darkMode}
            >
              {isDark ? <span className="text-xl">☀</span> : <span className="text-xl">☾</span>}
            </button>
            
            {/* <a
              href="https://ko-fi.com/C0C2QPCEG"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
              title="Support on Ko-fi"
            >
              Support
            </a> */}
            
            <a
              href="https://calendly.com/benjamin-juarez/60min"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary hidden sm:inline-flex items-center text-sm hover:scale-105 transition-transform"
            >
              {t.header.scheduleCall}
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
            >
              {isMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 dark:border-dark-700 animate-fade-in">
            <div className="flex flex-col space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    handleNavClick(item.id);
                    setIsMenuOpen(false);
                  }}
                  className={`px-3 py-2 rounded-md text-left transition-colors ${
                    activeSection === item.id
                      ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <a
                href="https://calendly.com/benjamin-juarez/60min"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-4 text-center"
              >
                {t.header.scheduleCall}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
